import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { ROL_LABEL } from '@leadsphere/types';

import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.js';

// Refleja Notificacion de apps/api/src/services/notificaciones.service.ts.
export interface Notificacion {
  id: string;
  usuario_id: string;
  tipo: 'seguimiento' | 'sistema' | 'cxc' | 'ticket';
  titulo: string;
  cuerpo: string;
  leido: boolean;
  link: string | null;
  created_at: string;
}

interface NotificacionesResponse {
  data: Notificacion[];
  unread: number;
}

function fmtFecha(iso: string): string {
  return new Date(iso).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Mismos labels que NAV_GROUPS/nav-config.tsx (grupo + item), expresado como
// lista plana igual que la versión anterior de este archivo — solo se agrega
// el nivel de grupo para el breadcrumb (restyle visual, mockups/base.html).
function breadcrumbParaRuta(pathname: string): { grupo: string; titulo: string } {
  if (pathname.startsWith('/cxc')) return { grupo: 'Principal', titulo: 'CxC' };
  if (pathname.startsWith('/reportes')) return { grupo: 'Principal', titulo: 'Reportes' };
  if (pathname.startsWith('/catalogos')) return { grupo: 'Gestión', titulo: 'Catálogos' };
  if (pathname.startsWith('/clientes')) return { grupo: 'Gestión', titulo: 'Clientes' };
  if (pathname.startsWith('/usuarios')) return { grupo: 'Sistema', titulo: 'Usuarios' };
  if (pathname.startsWith('/seguimientos')) return { grupo: 'Operaciones', titulo: 'Estado permisos' };
  if (pathname.startsWith('/tickets/dashboard')) return { grupo: 'Operaciones', titulo: 'Dashboard' };
  if (pathname.startsWith('/tickets')) return { grupo: 'Operaciones', titulo: 'Tickets' };
  if (pathname.startsWith('/aspirantes')) return { grupo: 'RH', titulo: 'Aspirantes' };
  return { grupo: '', titulo: 'Nexus' };
}

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function ShieldCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

export function Topbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { get, patch } = useApi();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // GET /notificaciones al montar (SRS-01 §11.1) — refleja el badge de no leídas
  const { data } = useQuery({
    queryKey: ['notificaciones'],
    queryFn: () => get<NotificacionesResponse>('/notificaciones'),
    staleTime: 30_000,
  });

  // Prompt AN — dropdown de la campanita (mismo patrón click-outside que
  // CheckboxDropdown.tsx). La campanita ya existía (ícono + badge); solo se
  // le agrega la lista desplegable.
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const marcarLeidaMutation = useMutation({
    mutationFn: (notifId: string) => patch(`/notificaciones/${notifId}/leer`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notificaciones'] }),
  });

  const marcarTodasMutation = useMutation({
    mutationFn: () => patch('/notificaciones/leer-todas', {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notificaciones'] }),
  });

  function handleNotifClick(n: Notificacion) {
    if (!n.leido) marcarLeidaMutation.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  }

  const notificaciones = data?.data ?? [];
  const unread = data?.unread ?? 0;
  const { grupo, titulo } = breadcrumbParaRuta(location.pathname);

  return (
    <header className="topbar">
      <div className="topbar-crumb">
        {grupo && (
          <>
            <span className="topbar-crumb-group">{grupo}</span>
            <span className="topbar-crumb-sep"><ChevronRightIcon /></span>
          </>
        )}
        <span className="topbar-title">{titulo}</span>
      </div>
      <div className="topbar-actions">
        {user && (
          <span className={`role-pill role-pill-${user.rol}`}>
            <ShieldCheckIcon />
            {ROL_LABEL[user.rol] ?? user.rol}
          </span>
        )}
        <div ref={wrapRef} style={{ position: 'relative' }}>
          <div
            className="topbar-bell"
            role="button"
            tabIndex={0}
            aria-label={`${unread} notificaciones sin leer`}
            onClick={() => setOpen(o => !o)}
          >
            <BellIcon />
            {unread > 0 && <span className="topbar-bell-badge">{unread > 99 ? '99+' : unread}</span>}
          </div>
          {open && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                width: 340,
                maxHeight: 400,
                overflowY: 'auto',
                background: '#fff',
                border: '1px solid var(--border)',
                borderRadius: 10,
                boxShadow: '0 10px 30px rgba(0, 0, 0, .13)',
                zIndex: 100,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--heading)' }}>Notificaciones</span>
                {unread > 0 && (
                  <button
                    type="button"
                    style={{ background: 'none', border: 'none', color: 'var(--brand)', fontSize: 11.5, cursor: 'pointer', padding: 0 }}
                    disabled={marcarTodasMutation.isPending}
                    onClick={() => marcarTodasMutation.mutate()}
                  >
                    Marcar todas leídas
                  </button>
                )}
              </div>
              {notificaciones.length === 0 ? (
                <div style={{ padding: '18px 14px', fontSize: 12.5, color: 'var(--muted)', textAlign: 'center' }}>Sin notificaciones</div>
              ) : (
                notificaciones.map(n => (
                  <div
                    key={n.id}
                    onClick={() => handleNotifClick(n)}
                    style={{
                      padding: '10px 14px',
                      borderBottom: '1px solid var(--border)',
                      cursor: 'pointer',
                      background: n.leido ? 'transparent' : '#eff6ff',
                    }}
                  >
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--heading)' }}>{n.titulo}</div>
                    <div style={{ fontSize: 12, color: 'var(--body)', marginTop: 2 }}>{n.cuerpo}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{fmtFecha(n.created_at)}</div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
